import React, { useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ImageRoundedIcon from '@mui/icons-material/ImageRounded';
import SpellcheckRoundedIcon from '@mui/icons-material/SpellcheckRounded';
import TipsAndUpdatesRoundedIcon from '@mui/icons-material/TipsAndUpdatesRounded';
import TouchAppRoundedIcon from '@mui/icons-material/TouchAppRounded';
import MiniTaskGameHeader from '../components/MiniTaskGameHeader';
import { playMiniTaskSound } from '../soundEffects';
import { withTurkishGenitive } from '../turkishSuffixes';
import useMiniTaskCountdown, { clearMiniTaskCountdown } from '../useMiniTaskCountdown';
import useSequenceSelection from '../components/useSequenceSelection';

const FEEDBACK_DELAY_MS = 700;
const EMPTY_HINT_POSITIONS = [];

function WordBuildClueImage({ src, alt, isEnglish, onError }) {
  const [loaded, setLoaded] = useState(false);
  const imageRef = useRef(null);
  useEffect(() => {
    if (imageRef.current?.complete && imageRef.current.naturalWidth > 0) setLoaded(true);
  }, []);
  return (
    <>
      {!loaded && <Box role="status" sx={{ position: 'absolute', textAlign: 'center', color: '#6d45df', px: 1 }}>
        <ImageRoundedIcon sx={{ fontSize: { xs: 56, md: 68 }, mb: 0.7 }} />
        <Typography sx={{ fontWeight: 900 }}>{isEnglish ? 'Image is being prepared' : 'Görsel hazırlanıyor'}</Typography>
      </Box>}
      <Box component="img" src={src} alt={alt}
        ref={imageRef}
        onLoad={() => setLoaded(true)} onError={onError}
        sx={{ display: 'block', visibility: loaded ? 'visible' : 'hidden', width: '100%', height: '100%', objectFit: 'contain', borderRadius: 2.5 }} />
    </>
  );
}

function idempotencyKey() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `word-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function targetPositionFromTileId(tileId, letterCount) {
  const match = String(tileId || '').match(/:(\d+)$/);
  if (!match) return null;
  const position = Number(match[1]);
  return Number.isInteger(position) && position >= 0 && position < letterCount
    ? position
    : null;
}

export function pickWordBuildHintPosition(targetPositions, revealedPositions, randomValue = Math.random()) {
  const revealed = new Set(revealedPositions);
  const candidates = targetPositions.filter((position) => !revealed.has(position));
  if (candidates.length <= 1) return null;
  const safeRandomValue = Math.min(Math.max(Number(randomValue) || 0, 0), 0.999999);
  return candidates[Math.floor(safeRandomValue * candidates.length)];
}

function WordBuildBackdrop() {
  return (
    <Box aria-hidden sx={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
      <Box sx={{ position: 'absolute', left: '-5%', top: '31%', width: 132, height: 43, borderRadius: 99, bgcolor: 'rgba(255,255,255,0.86)', '&::before, &::after': { content: '""', position: 'absolute', borderRadius: '50%', bgcolor: 'inherit' }, '&::before': { width: 58, height: 58, left: 20, top: -26 }, '&::after': { width: 72, height: 72, right: 12, top: -36 } }} />
      <Box sx={{ position: 'absolute', right: '-4%', top: '38%', width: 122, height: 40, borderRadius: 99, bgcolor: 'rgba(255,255,255,0.82)', '&::before, &::after': { content: '""', position: 'absolute', borderRadius: '50%', bgcolor: 'inherit' }, '&::before': { width: 52, height: 52, left: 17, top: -23 }, '&::after': { width: 66, height: 66, right: 9, top: -33 } }} />
      <Box sx={{ position: 'absolute', left: { xs: -28, md: 42 }, top: { xs: '70%', md: '67%' }, width: { xs: 86, md: 112 }, height: { xs: 27, md: 34 }, borderRadius: 99, bgcolor: 'rgba(255,255,255,0.58)', '&::before, &::after': { content: '""', position: 'absolute', borderRadius: '50%', bgcolor: 'inherit' }, '&::before': { width: '42%', aspectRatio: '1', left: '18%', top: '-58%' }, '&::after': { width: '52%', aspectRatio: '1', right: '10%', top: '-82%' } }} />
      <Box sx={{ position: 'absolute', right: { xs: -30, md: 52 }, top: { xs: '76%', md: '72%' }, width: { xs: 92, md: 118 }, height: { xs: 28, md: 36 }, borderRadius: 99, bgcolor: 'rgba(255,255,255,0.5)', '&::before, &::after': { content: '""', position: 'absolute', borderRadius: '50%', bgcolor: 'inherit' }, '&::before': { width: '44%', aspectRatio: '1', left: '16%', top: '-60%' }, '&::after': { width: '54%', aspectRatio: '1', right: '9%', top: '-86%' } }} />
      <Box sx={{ position: 'absolute', left: '-8%', right: '-8%', bottom: -82, height: { xs: 205, md: 250 }, borderRadius: '50% 50% 0 0', bgcolor: '#a7d84b', transform: 'rotate(-1.5deg)', boxShadow: 'inset 0 18px 0 rgba(255,255,255,0.14)' }} />
      <Box sx={{ position: 'absolute', left: '56%', right: '-12%', bottom: -72, height: { xs: 190, md: 228 }, borderRadius: '50% 50% 0 0', bgcolor: '#84c63e', transform: 'rotate(-8deg)' }} />
      <Box sx={{ position: 'absolute', left: '-14%', width: '45%', bottom: -78, height: { xs: 182, md: 220 }, borderRadius: '50% 50% 0 0', bgcolor: '#94cf46', transform: 'rotate(7deg)' }} />
      <Box sx={{ position: 'absolute', left: { xs: 20, md: 96 }, bottom: { xs: 70, md: 92 }, color: '#facc15', fontSize: { xs: 29, md: 43 }, transform: 'rotate(-14deg)', textShadow: '24px 12px 0 #fb7185' }}>★</Box>
      <Box sx={{ position: 'absolute', right: { xs: 28, md: 118 }, bottom: { xs: 76, md: 100 }, color: '#fff', fontSize: { xs: 25, md: 38 }, textShadow: '-24px 9px 0 #f9a8d4' }}>✿</Box>
      <Box sx={{ position: 'absolute', left: { xs: 18, md: 112 }, top: '48%', color: '#c084fc', fontSize: { xs: 22, md: 30 } }}>✦</Box>
      <Box sx={{ position: 'absolute', right: { xs: 22, md: 132 }, top: '53%', color: '#93c5fd', fontSize: { xs: 21, md: 28 } }}>✦</Box>
    </Box>
  );
}

const tileTones = [
  { background: '#fff7dc', border: '#f59e0b', shadow: '#d97706', color: '#78350f' },
  { background: '#f3efff', border: '#a78bfa', shadow: '#7c3aed', color: '#3b237f' },
  { background: '#eaf8ff', border: '#60a5fa', shadow: '#2563eb', color: '#173f76' },
  { background: '#edfff3', border: '#4ade80', shadow: '#16a34a', color: '#166534' },
];

export default function WordBuildRoundView({
  round,
  boardSessionId,
  classTitle,
  soundEnabled,
  onToggleSound,
  onExit,
  onAttempt,
  onRoundChange,
  onComplete,
  onTimeout,
  assetUrls = {},
}) {
  const word = round?.current_word;
  const isEnglish = round?.lesson?.code === 'ENGLISH';
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [mediaFailed, setMediaFailed] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const [timeoutSaving, setTimeoutSaving] = useState(false);
  const [timeoutError, setTimeoutError] = useState('');
  const [revealedHintState, setRevealedHintState] = useState({ questionId: '', positions: [] });
  const { selectedIds, setSelectedIds, select, remove } = useSequenceSelection({
    questionKey: `${word?.question_id || ''}:${word?.attempt_count || 0}`,
    capacity: Math.max(0, Number(word?.letter_count || 0) - (revealedHintState.questionId === String(word?.question_id || '') ? revealedHintState.positions.length : 0)),
    tokenIds: (word?.tiles || []).map((tile) => String(tile.id)),
  });
  const submitSignatureRef = useRef('');
  const attemptKeyRef = useRef({ signature: '', key: '' });
  const timedOutRef = useRef(false);
  const timeoutSavingRef = useRef(false);
  const transitionTimerRef = useRef(null);
  const mountedRef = useRef(true);
  const questionKey = `${word?.question_id || ''}:${word?.attempt_count || 0}`;
  const remainingSeconds = useMiniTaskCountdown({
    roundId: round?.round_id,
    boardSessionId,
    durationSeconds: round?.duration_seconds || 120,
    active: Boolean(word && !timedOut && !(round.preview && round.duration_seconds === null)),
    onExpire: () => finishOnTimeout(),
  });

  const finishOnTimeout = async () => {
    if (timeoutSavingRef.current) return;
    timedOutRef.current = true;
    timeoutSavingRef.current = true;
    setTimedOut(true);
    setTimeoutSaving(true);
    setTimeoutError('');
    if (transitionTimerRef.current) window.clearTimeout(transitionTimerRef.current);
    try {
      const summary = await onTimeout();
      if (!mountedRef.current) return;
      clearMiniTaskCountdown(round.round_id);
      onComplete(summary);
    } catch (error) {
      if (!mountedRef.current) return;
      setTimeoutError(error?.message || (isEnglish ? 'Your result could not be saved.' : 'Sonucun kaydedilemedi.'));
      timeoutSavingRef.current = false;
      setTimeoutSaving(false);
    }
  };
  const tiles = useMemo(() => word?.tiles || [], [word?.tiles]);
  const tileById = useMemo(
    () => new Map(tiles.map((tile) => [String(tile.id), tile])),
    [tiles],
  );
  const selectedTiles = selectedIds.map((id) => tileById.get(id)).filter(Boolean);
  const letterCount = Number(word?.letter_count || 0);
  const currentQuestionId = String(word?.question_id || '');
  const revealedPositions = revealedHintState.questionId === currentQuestionId
    ? revealedHintState.positions
    : EMPTY_HINT_POSITIONS;
  const revealedPositionSet = useMemo(
    () => new Set(revealedPositions),
    [revealedPositions],
  );
  const targetTileByPosition = useMemo(() => {
    const result = new Map();
    tiles.forEach((tile) => {
      const position = targetPositionFromTileId(tile.id, letterCount);
      if (position != null) result.set(position, tile);
    });
    return result;
  }, [letterCount, tiles]);
  const targetPositions = useMemo(
    () => [...targetTileByPosition.keys()].sort((left, right) => left - right),
    [targetTileByPosition],
  );
  const openPositionCount = Math.max(0, letterCount - revealedPositions.length);
  const answerTiles = useMemo(() => {
    let selectedIndex = 0;
    return Array.from({ length: letterCount }, (_, position) => {
      if (revealedPositionSet.has(position)) return targetTileByPosition.get(position) || null;
      const selectedTile = selectedTiles[selectedIndex] || null;
      selectedIndex += 1;
      return selectedTile;
    });
  }, [letterCount, revealedPositionSet, selectedTiles, targetTileByPosition]);
  const answerGroups = useMemo(() => {
    const displayWord = String(word?.display_word || '').trim();
    if (!displayWord || !isEnglish) {
      return [Array.from({ length: letterCount }, (_, position) => ({ type: 'letter', position }))];
    }
    let position = 0;
    return displayWord.split(/\s+/).map((group) => [...group].map((character) => {
      if (/[A-Za-z]/.test(character)) {
        const item = { type: 'letter', position };
        position += 1;
        return item;
      }
      return { type: 'separator', value: character };
    }));
  }, [isEnglish, letterCount, word?.display_word]);

  useEffect(() => {
    setSelectedIds([]);
    setSubmitting(false);
    setFeedback(null);
    submitSignatureRef.current = '';
    attemptKeyRef.current = { signature: '', key: '' };
  }, [questionKey, setSelectedIds]);

  useEffect(() => {
    setMediaFailed(false);
  }, [questionKey, word?.media?.url]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (transitionTimerRef.current) window.clearTimeout(transitionTimerRef.current);
    };
  }, []);

  const submitAnswer = async (chosenIds) => {
    let chosenIndex = 0;
    const chosenAnswer = Array.from({ length: letterCount }, (_, position) => {
      if (revealedPositionSet.has(position)) {
        return targetTileByPosition.get(position)?.letter || '';
      }
      const tileId = chosenIds[chosenIndex];
      chosenIndex += 1;
      return tileById.get(tileId)?.letter || '';
    }).join('');
    const signature = `${questionKey}:${chosenIds.join(',')}`;
    if (timedOutRef.current || remainingSeconds === 0 || !word?.question_id || chosenAnswer.length !== letterCount || submitSignatureRef.current === signature) return;
    submitSignatureRef.current = signature;
    const requestKey = attemptKeyRef.current.signature === signature
      ? attemptKeyRef.current.key
      : idempotencyKey();
    attemptKeyRef.current = { signature, key: requestKey };
    setSubmitting(true);
    try {
      const payload = await onAttempt({
        questionId: word.question_id,
        answer: chosenAnswer,
        idempotencyKey: requestKey,
      });
      if (!mountedRef.current || timedOutRef.current) return;
      const correct = Boolean(payload?.attempt_result?.correct);
      const hintPosition = correct
        ? null
        : pickWordBuildHintPosition(targetPositions, revealedPositions);
      if (hintPosition != null) {
        setRevealedHintState({
          questionId: currentQuestionId,
          positions: [...revealedPositions, hintPosition],
        });
        setSelectedIds([]);
      }
      setFeedback({
        type: correct ? 'correct' : 'wrong',
        message: correct
          ? (isEnglish ? 'Great job, correct!' : 'Harika, doğru!')
          : hintPosition != null
            ? (isEnglish ? 'Almost! A hint was revealed ✨' : 'Çok yaklaştın! Bir ipucu açıldı ✨')
            : (isEnglish ? "Let's try again!" : 'Bir daha deneyelim!'),
      });
      playMiniTaskSound(correct ? 'correct' : 'wrong', soundEnabled);
      transitionTimerRef.current = window.setTimeout(() => {
        if (!mountedRef.current || timedOutRef.current) return;
        if (payload?.round_completed) {
          clearMiniTaskCountdown(round.round_id);
          playMiniTaskSound('complete', soundEnabled);
          onComplete(payload.summary);
        } else {
          onRoundChange(payload.round);
        }
      }, FEEDBACK_DELAY_MS);
    } catch (error) {
      if (!mountedRef.current || timedOutRef.current) return;
      setSubmitting(false);
      setFeedback({
        type: 'error',
        message: error?.message || (isEnglish ? 'Your answer could not be saved.' : 'Cevap kaydedilemedi.'),
      });
    }
  };

  const selectTile = (tileId) => {
    if (timedOut || remainingSeconds === 0 || submitting || feedback || selectedIds.length >= openPositionCount) return;
    const nextSelectedIds = select(tileId);
    if (!nextSelectedIds) return;
    if (nextSelectedIds.length === openPositionCount) submitAnswer(nextSelectedIds);
  };

  const removeTile = (tileId) => {
    if (timedOut || remainingSeconds === 0 || submitting || feedback) return;
    remove(tileId);
  };

  const retryAfterError = () => {
    submitSignatureRef.current = '';
    setFeedback(null);
    submitAnswer(selectedIds);
  };

  if (!word) return null;
  const showVisual = word.question_type === 'image' || word.question_type === 'image_clue';
  const showClue = word.question_type !== 'image' && Boolean(word.prompt);
  const completed = Number(round?.completed_word_count || 0);
  const questionCount = Number(round?.question_count || 3);
  const studentName = round?.student?.display_name || (isEnglish ? 'Student' : 'Öğrenci');

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        width: '100%',
        maxWidth: '100vw',
        bgcolor: '#fffaf0',
        color: '#172554',
        overflowX: 'hidden',
        backgroundImage: 'linear-gradient(180deg, #fffaf0 0%, #fff8ea 70%, #f7f4d6 100%)',
      }}
    >
      {round?.preview ? (
        <Box sx={{ position: 'fixed', bottom: 12, left: 12, zIndex: 20, bgcolor: '#facc15', color: '#713f12', px: 1.5, py: 0.5, borderRadius: 99, fontWeight: 1000, boxShadow: '0 3px 0 rgba(146,64,14,0.16)' }}>
          TEST MODU
        </Box>
      ) : null}
      <WordBuildBackdrop />

      <Box sx={{ position: 'relative', zIndex: 1, mx: 'auto', width: '100%', maxWidth: 1720, boxSizing: 'border-box', px: { xs: 1.2, sm: 2, md: 3.2 }, pb: { xs: 11, md: 13 } }}>
        <MiniTaskGameHeader isEndless={Boolean(round?.is_endless)} classTitle={classTitle} soundEnabled={soundEnabled} onToggleSound={onToggleSound} />

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr auto', md: '220px minmax(0, 1fr) 220px' },
            alignItems: 'center',
            gap: { xs: 1, md: 2 },
            mt: { xs: 1.7, md: 3.2 },
            mb: { xs: 3.2, md: 2.8 },
            '@media (max-height: 820px) and (min-width: 600px)': { mt: 1.7, mb: 1.4 },
          }}
        >
          <ButtonBase
            aria-label={isEnglish ? 'Back to students' : 'Öğrenci listesine dön'}
            onClick={onExit}
            sx={{
              justifySelf: 'start',
              gridColumn: { xs: 1, md: 1 },
              gridRow: 1,
              borderRadius: 3,
              border: '2px solid #d8b4fe',
              bgcolor: 'rgba(255,255,255,0.9)',
              color: '#33258a',
              px: { xs: 1.3, md: 2.2 },
              py: { xs: 0.85, md: 1.2 },
              minWidth: { xs: 0, sm: 142, md: 210 },
              minHeight: { xs: 50, md: 60 },
              boxShadow: '0 4px 0 rgba(91,33,182,0.12)',
              display: 'flex',
              alignItems: 'center',
              gap: 0.8,
              '&:hover': { bgcolor: '#fff', transform: 'translateY(-1px)' },
            }}
          >
            <ArrowBackRoundedIcon sx={{ fontSize: { xs: 24, md: 31 } }} />
            <Typography sx={{ fontWeight: 1000, fontSize: { xs: 16, md: 25 }, lineHeight: 1, whiteSpace: 'nowrap' }}>{isEnglish ? 'Home' : 'Ana sayfa'}</Typography>
          </ButtonBase>

          <Typography
            sx={{
              gridColumn: { xs: '1 / -1', md: 2 },
              gridRow: { xs: 2, md: 1 },
              textAlign: 'center',
              color: '#251777',
              fontWeight: 1000,
              fontSize: { xs: studentName.length > 12 ? 24 : 28, sm: 40, md: 50 },
              lineHeight: 1,
              overflowWrap: 'anywhere',
              textShadow: '0 3px 0 rgba(255,255,255,0.94)',
            }}
          >
            {isEnglish ? `${studentName}'s Mini Task` : `${withTurkishGenitive(studentName)} Mini Görevi`}
          </Typography>

          <Box sx={{ gridColumn: { xs: 2, md: 3 }, gridRow: 1, justifySelf: 'end', borderRadius: 3, border: '2px solid #d8b4fe', bgcolor: 'rgba(255,255,255,0.9)', color: '#33258a', px: { xs: 1.15, md: 2.2 }, py: { xs: 0.85, md: 1.2 }, minWidth: { xs: 0, sm: 126, md: 210 }, minHeight: { xs: 50, md: 60 }, boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', boxShadow: '0 4px 0 rgba(91,33,182,0.12)' }}>
            <Box>
              <Typography sx={{ fontWeight: 1000, fontSize: { xs: 16, md: 25 }, lineHeight: 1, whiteSpace: 'nowrap' }}>{isEnglish ? 'Word' : 'Kelime'} {completed + 1} / {questionCount}</Typography>
              {!(round.preview && round.duration_seconds === null) && <Typography role="timer" sx={{ fontWeight: 900, fontSize: { xs: 14, md: 19 } }}>{isEnglish ? 'Time' : 'Süre'}: {remainingSeconds}</Typography>}
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            width: '100%',
            maxWidth: 1180,
            boxSizing: 'border-box',
            mx: 'auto',
            position: 'relative',
            borderRadius: { xs: 3.5, md: 4.5 },
            bgcolor: 'rgba(255,255,255,0.96)',
            border: '2px solid #e9d5ff',
            boxShadow: '0 15px 35px rgba(76,29,149,0.14), inset 0 0 0 2px rgba(255,255,255,0.84)',
            px: { xs: 1.3, sm: 2.5, md: 4.5 },
            pt: { xs: 4.2, sm: 4.7, md: 5.2 },
            pb: { xs: 1.5, sm: 2, md: 2.4 },
          }}
        >
          <Box sx={{ position: 'absolute', left: '50%', top: 0, transform: 'translate(-50%, -50%)', borderRadius: 2.5, border: '2px solid #d8b4fe', bgcolor: '#f5f0ff', color: '#6d45df', px: { xs: 1.4, md: 2.2 }, py: { xs: 0.55, md: 0.75 }, display: 'flex', alignItems: 'center', gap: 0.8, minWidth: { xs: 205, md: 290 }, justifyContent: 'center', boxShadow: '0 4px 0 rgba(91,33,182,0.10)' }}>
            <SpellcheckRoundedIcon sx={{ color: '#7c3aed', fontSize: { xs: 27, md: 34 } }} />
            <Typography sx={{ fontWeight: 1000, fontSize: { xs: 18, md: 24 }, lineHeight: 1 }}>{isEnglish ? 'Word Builder' : 'Kelimeyi Kur'}</Typography>
          </Box>

          <Box sx={{ position: 'absolute', left: { xs: 17, md: 58 }, top: { xs: 48, md: 62 }, color: '#a78bfa', fontSize: { xs: 22, md: 30 }, fontWeight: 1000 }}>✦</Box>
          <Box sx={{ position: 'absolute', right: { xs: 18, md: 62 }, top: { xs: 56, md: 72 }, color: '#93c5fd', fontSize: { xs: 19, md: 26 }, fontWeight: 1000 }}>✦</Box>

          {timedOut && (
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography sx={{ fontWeight: 900, color: '#b45309' }}>{timeoutSaving ? 'Süre doldu, sonuç kaydediliyor...' : timeoutError || 'Süre doldu.'}</Typography>
              {timeoutError && <Button onClick={finishOnTimeout}>{isEnglish ? 'Try again' : 'Tekrar dene'}</Button>}
            </Box>
          )}

          {showVisual ? (
            word.media?.url && !mediaFailed ? (
              <Box sx={{ position: 'relative', width: { xs: 'min(72vw, 300px)', sm: 350, md: 390 }, aspectRatio: '1 / 1', mx: 'auto', borderRadius: { xs: 3, md: 3.5 }, bgcolor: '#f8fafc', border: '2px solid #e0e7ff', boxShadow: '0 7px 0 rgba(124,58,237,0.08), 0 14px 26px rgba(15,23,42,0.08)', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 0.5 }}>
                <WordBuildClueImage
                  key={`${word.question_id}:${assetUrls[word.media.url] || word.media.url}`}
                  src={assetUrls[word.media.url] || word.media.url}
                  alt={word.media.alt || (isEnglish ? 'Word clue image' : 'Kelime ipucu görseli')}
                  isEnglish={isEnglish}
                  onError={() => setMediaFailed(true)}
                />
              </Box>
            ) : (
              <Box sx={{ width: { xs: 'min(72vw, 300px)', sm: 350, md: 390 }, aspectRatio: '1 / 1', mx: 'auto', borderRadius: { xs: 3, md: 3.5 }, bgcolor: '#f8fafc', border: '2px dashed #c4b5fd', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#6d45df' }}>
                <ImageRoundedIcon sx={{ fontSize: { xs: 56, md: 68 }, mb: 0.7 }} />
                <Typography sx={{ fontWeight: 900 }}>{isEnglish ? 'Image is being prepared' : 'Görsel hazırlanıyor'}</Typography>
              </Box>
            )
          ) : null}

          {showClue ? (
            showVisual ? (
              <Typography sx={{ textAlign: 'center', maxWidth: 820, mx: 'auto', mt: { xs: 1.6, md: 2 }, mb: 0.6, px: 1, fontWeight: 1000, fontSize: { xs: 21, sm: 27, md: 32 }, lineHeight: 1.16, color: '#251777', textShadow: '0 2px 0 #fff' }}>
                {word.prompt}
              </Typography>
            ) : (
              <Box sx={{ width: 'fit-content', maxWidth: { xs: '94%', sm: 760 }, mx: 'auto', mt: { xs: 1.2, md: 1.8 }, mb: { xs: 0.9, md: 1.2 }, px: { xs: 1.6, sm: 2.4, md: 3 }, py: { xs: 1.25, md: 1.6 }, borderRadius: 3, bgcolor: '#f5f0ff', border: '2px solid #ddd0ff', boxShadow: '0 5px 0 rgba(109,40,217,0.10), 0 12px 24px rgba(76,29,149,0.08)', textAlign: 'center' }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.55, mb: 0.45, color: '#7c3aed' }}>
                  <TipsAndUpdatesRoundedIcon sx={{ fontSize: { xs: 19, md: 23 }, color: '#f59e0b', filter: 'drop-shadow(0 2px 0 rgba(217,119,6,0.18))' }} />
                  <Typography sx={{ fontWeight: 1000, fontSize: { xs: 13, md: 16 }, lineHeight: 1, letterSpacing: 0.3 }}>{isEnglish ? 'Hint' : 'İpucu'}</Typography>
                </Box>
                <Typography sx={{ fontWeight: 1000, fontSize: { xs: 20, sm: 26, md: 31 }, lineHeight: 1.16, color: '#251777', textShadow: '0 2px 0 #fff' }}>
                  {word.prompt}
                </Typography>
              </Box>
            )
          ) : null}

          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.8, mt: { xs: 1.7, md: 2.1 }, mb: { xs: 1.4, md: 1.8 }, color: '#4f4a78' }}>
            <Box sx={{ width: { xs: 31, md: 38 }, height: { xs: 31, md: 38 }, borderRadius: '50%', bgcolor: '#ede9fe', color: '#7c3aed', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 3px 0 rgba(109,40,217,0.15)' }}>
              <TouchAppRoundedIcon sx={{ fontSize: { xs: 20, md: 25 } }} />
            </Box>
            <Typography sx={{ textAlign: 'center', fontSize: { xs: 16, sm: 18, md: 21 }, fontWeight: 900 }}>
              {isEnglish ? 'Tap the letters to build the word.' : 'Harflere dokun, kelimeyi oluştur.'}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', columnGap: { xs: 1.5, sm: 2.2 }, rowGap: 1, width: '100%', mx: 'auto', mb: { xs: 2.2, md: 2.8 } }} aria-label={isEnglish ? 'Answer slots' : 'Cevap yuvaları'}>
            {answerGroups.map((group, groupIndex) => (
              <Box key={groupIndex} sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.35, sm: 0.8, md: 1 } }}>
                {group.map((part, partIndex) => {
                  if (part.type === 'separator') {
                    return <Typography key={`separator-${partIndex}`} aria-hidden sx={{ color: '#4f4a78', fontWeight: 1000, fontSize: { xs: 24, sm: 31, md: 34 } }}>{part.value}</Typography>;
                  }
                  const index = part.position;
                  const tile = answerTiles[index];
                  const revealed = revealedPositionSet.has(index);
                  const slotLabel = revealed
                    ? (isEnglish ? `${tile?.letter || ''} revealed as a hint` : `${tile?.letter || ''} harfi ipucu olarak açıldı`)
                    : tile
                      ? (isEnglish ? `Remove letter ${tile.letter}` : `${tile.letter} harfini geri al`)
                      : (isEnglish ? `Empty letter slot ${index + 1}` : `${index + 1}. boş harf yuvası`);
                  return (
                    <Button
                      key={index}
                      aria-label={slotLabel}
                      onClick={() => tile && !revealed && removeTile(tile.id)}
                      disabled={!tile || timedOut || remainingSeconds === 0 || submitting || revealed}
                      sx={{
                        minWidth: { xs: letterCount > 10 ? 31 : 38, sm: 50, md: 58 },
                        width: { xs: letterCount > 10 ? 31 : 42, sm: 54, md: 62 },
                        height: { xs: letterCount > 10 ? 36 : 46, sm: 58, md: 66 },
                        p: 0,
                        borderRadius: { xs: 1.4, md: 2.3 },
                        border: `3px solid ${feedback?.type === 'correct' ? '#22c55e' : revealed ? '#f59e0b' : feedback?.type === 'wrong' ? '#fb7185' : tile ? '#8b5cf6' : '#d6cced'}`,
                        bgcolor: revealed ? '#fff7dc' : tile ? '#f5f0ff' : '#fff',
                        color: revealed ? '#92400e' : '#251777',
                        fontWeight: 1000,
                        fontSize: { xs: letterCount > 10 ? 18 : 23, sm: 31, md: 34 },
                        boxShadow: revealed ? '0 4px 0 rgba(217,119,6,0.22), 0 0 18px rgba(250,204,21,0.24)' : tile ? '0 4px 0 rgba(109,40,217,0.14)' : 'inset 0 -4px 0 rgba(226,232,240,0.42)',
                        animation: revealed ? 'wordHintReveal 420ms ease-out' : 'none',
                        '@keyframes wordHintReveal': {
                          '0%': { transform: 'scale(0.65) rotate(-8deg)', opacity: 0 },
                          '65%': { transform: 'scale(1.1) rotate(3deg)', opacity: 1 },
                          '100%': { transform: 'scale(1) rotate(0)' },
                        },
                        '&.Mui-disabled': { color: revealed ? '#92400e' : tile ? '#251777' : 'transparent', bgcolor: revealed ? '#fff7dc' : tile ? '#f5f0ff' : '#fff' },
                      }}
                    >
                      {tile?.letter || '·'}
                    </Button>
                  );
                })}
              </Box>
            ))}
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: { xs: 0.85, sm: 1.15, md: 1.35 }, rowGap: { xs: 1.2, md: 1.5 }, flexWrap: 'wrap', maxWidth: 920, mx: 'auto' }} aria-label={isEnglish ? 'Letter pool' : 'Harf havuzu'}>
            {tiles.map((tile, index) => {
              const selected = selectedIds.includes(String(tile.id));
              const revealed = revealedPositions.some(
                (position) => String(targetTileByPosition.get(position)?.id) === String(tile.id),
              );
              const tone = tileTones[index % tileTones.length];
              return (
                <Button
                  key={tile.id}
                  aria-label={isEnglish ? `Letter ${tile.letter}` : `${tile.letter} harfi`}
                  disabled={selected || revealed || timedOut || remainingSeconds === 0 || submitting || Boolean(feedback)}
                  onClick={() => selectTile(tile.id)}
                  sx={{
                    minWidth: { xs: 50, sm: 62, md: 70 },
                    height: { xs: 52, sm: 62, md: 68 },
                    px: { xs: 1.2, md: 1.6 },
                    borderRadius: { xs: 2.2, md: 2.7 },
                    bgcolor: selected || revealed ? '#e2e8f0' : tone.background,
                    border: `2px solid ${selected || revealed ? '#cbd5e1' : tone.border}`,
                    color: tone.color,
                    fontSize: { xs: 24, sm: 30, md: 34 },
                    fontWeight: 1000,
                    boxShadow: selected || revealed ? 'none' : `0 5px 0 ${tone.shadow}, 0 9px 14px rgba(15,23,42,0.08)`,
                    transform: selected || revealed ? 'translateY(3px)' : 'none',
                    transition: 'transform 130ms ease, box-shadow 130ms ease',
                    '&:hover': { bgcolor: selected || revealed ? '#e2e8f0' : tone.background, transform: selected || revealed ? 'translateY(3px)' : 'translateY(-2px)', boxShadow: selected || revealed ? 'none' : `0 7px 0 ${tone.shadow}, 0 12px 18px rgba(15,23,42,0.10)` },
                    '&.Mui-disabled': { color: selected || revealed ? 'transparent' : tone.color, borderColor: selected || revealed ? '#cbd5e1' : tone.border },
                  }}
                >
                  {tile.letter}
                </Button>
              );
            })}
          </Box>

          <Box sx={{ minHeight: { xs: 56, md: 66 }, mt: { xs: 1.6, md: 2 }, display: 'flex', alignItems: 'center', justifyContent: 'center' }} aria-live="polite">
            {submitting && !feedback ? (
              <Typography sx={{ fontWeight: 1000, color: '#6d45df', fontSize: { xs: 17, md: 21 } }}>{isEnglish ? 'Checking…' : 'Kontrol ediliyor…'}</Typography>
            ) : feedback ? (
              <Box sx={{ textAlign: 'center', minWidth: { xs: '85%', sm: 340 }, px: 2, py: 0.9, borderRadius: 99, bgcolor: feedback.type === 'correct' ? '#ecfdf3' : feedback.type === 'wrong' ? '#fff1f2' : '#fff7ed', border: `2px solid ${feedback.type === 'correct' ? '#86efac' : feedback.type === 'wrong' ? '#fda4af' : '#fdba74'}` }}>
                <Typography sx={{ fontWeight: 1000, fontSize: { xs: 19, sm: 23 }, color: feedback.type === 'correct' ? '#15803d' : feedback.type === 'wrong' ? '#be123c' : '#b45309' }}>
                  {feedback.message}
                </Typography>
                {feedback.type === 'error' ? (
                  <Button onClick={retryAfterError} sx={{ mt: 0.5, fontWeight: 900 }}>{isEnglish ? 'Try again' : 'Tekrar dene'}</Button>
                ) : null}
              </Box>
            ) : null}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
